export interface FilterStage {
  name: string;
  before: number;
  after: number;
  filtered: number;
}

export interface SearchLogSummary {
  keyword: string;
  stages: FilterStage[];
  summary: {
    totalPosts: number;
    uniqueCreators: number;
    withEmail: number;
    addedCreators: number;
  };
}

export function parseSearchLogs(logs: string[]): SearchLogSummary {
  const summary: SearchLogSummary = {
    keyword: '',
    stages: [],
    summary: {
      totalPosts: 0,
      uniqueCreators: 0,
      withEmail: 0,
      addedCreators: 0
    }
  };

  // Parse logs line by line
  logs.forEach(log => {
    // Extract keyword
    if (log.includes('Starting TikTok search for keyword:')) {
      const match = log.match(/keyword:\s*"([^"]+)"/);
      if (match) summary.keyword = match[1];
    }

    // Extract total posts from API
    if (log.includes('Received') && log.includes('posts from API')) {
      const match = log.match(/Received\s+(\d+)\s+posts/);
      if (match) summary.summary.totalPosts = parseInt(match[1]);
    }

    // Extract min views filter
    if (log.includes('Filtered:') && log.includes('→')) {
      const match = log.match(/Filtered:\s*(\d+)\s*→\s*(\d+)\s*posts\s*\((\d+)\s*excluded/);
      if (match) {
        summary.stages.push({
          name: 'Min views filter',
          before: parseInt(match[1]),
          after: parseInt(match[2]),
          filtered: parseInt(match[3])
        });
      }
    }

    // Extract existing creators filter
    if (log.includes('Found') && log.includes('existing creators to exclude')) {
      const match = log.match(/Found\s+(\d+)\s+existing creators/);
      if (match) {
        const existingCount = parseInt(match[1]);
        // We need to calculate posts filtered based on previous stage
        const prevStage = summary.stages[summary.stages.length - 1];
        if (prevStage) {
          // This will be updated when we see the filter pipeline log
          summary.stages.push({
            name: 'Existing creators filter',
            before: prevStage.after,
            after: 0, // Will be updated
            filtered: 0 // Will be updated
          });
        }
      }
    }

    // Extract final filter pipeline
    if (log.includes('Filter pipeline:')) {
      const match = log.match(/(\d+)\s*initial\s*→\s*(\d+)\s*\(min views\)\s*→\s*(\d+)\s*\(after excluding existing\)\s*→\s*(\d+)\s*creators/);
      if (match) {
        const initial = parseInt(match[1]);
        const afterMinViews = parseInt(match[2]);
        const afterExisting = parseInt(match[3]);
        const uniqueCreators = parseInt(match[4]);

        // Update or add stages with correct numbers
        summary.stages = [
          {
            name: 'Min views filter',
            before: initial,
            after: afterMinViews,
            filtered: initial - afterMinViews
          },
          {
            name: 'Existing creators filter',
            before: afterMinViews,
            after: afterExisting,
            filtered: afterMinViews - afterExisting
          },
          {
            name: 'Unique creators',
            before: afterExisting,
            after: uniqueCreators,
            filtered: 0 // This is grouping, not filtering
          }
        ];

        summary.summary.uniqueCreators = uniqueCreators;
      }
    }

    // Extract search completed info
    if (log.includes('Search completed:')) {
      const match = log.match(/Found\s+(\d+)\s+creators/);
      if (match) {
        summary.summary.uniqueCreators = parseInt(match[1]);
      }
    }

    // Extract added creators info
    if (log.includes('creators added to database')) {
      const match = log.match(/(\d+)\s+creators added/);
      if (match) {
        summary.summary.addedCreators = parseInt(match[1]);
      }
    }

    // Extract email info (new format: "X creators with email, Y without email")
    if (log.includes('creators with email')) {
      const match = log.match(/(\d+)\s+creators with email,\s*(\d+)\s+without email/);
      if (match) {
        summary.summary.withEmail = parseInt(match[1]);
        // Can also extract without email count if needed
      } else {
        // Fallback to old format
        const simpleMatch = log.match(/(\d+)\s+creators with email/);
        if (simpleMatch) {
          summary.summary.withEmail = parseInt(simpleMatch[1]);
        }
      }
    }
  });

  return summary;
}

export function formatLogSummary(summary: SearchLogSummary): string[] {
  const lines: string[] = [];

  lines.push(`📊 Search Summary for "${summary.keyword}"`);
  lines.push('────────────────────────────────');
  lines.push(`Initial posts found: ${summary.summary.totalPosts}`);
  lines.push('');
  lines.push('📌 Filtering Pipeline:');

  summary.stages.forEach((stage, index) => {
    const isLast = index === summary.stages.length - 1;
    const prefix = isLast ? '└─' : '├─';

    if (stage.name === 'Unique creators') {
      lines.push(`${prefix} ${stage.name}: ${stage.before} posts → ${stage.after} creators`);
    } else {
      lines.push(`${prefix} ${stage.name}: ${stage.before} → ${stage.after} posts (-${stage.filtered})`);
    }
  });

  lines.push('');
  lines.push('📧 Email Status:');
  lines.push(`├─ With email: ${summary.summary.withEmail} creators`);
  lines.push(`└─ Without email: ${summary.summary.uniqueCreators - summary.summary.withEmail} creators`);

  lines.push('');
  if (summary.summary.addedCreators > 0) {
    lines.push(`✅ Result: ${summary.summary.addedCreators} creators added to database`);
  } else {
    lines.push(`⚠️ Result: 0 creators added (${summary.summary.uniqueCreators} found without email)`);
  }

  return lines;
}